import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Clock, Plus, Trash2, Send, Save, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Search,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyEntry = () => ({
  id: crypto.randomUUID(),
  task_name: '',
  description: '',
  category: '',
  minutes: 0,
});

const TARGET = 480;

const fmtMin = (m) => {
  const h = Math.floor(m / 60);
  const mins = m % 60;
  if (h === 0) return `${mins}m`;
  return mins ? `${h}h ${mins}m` : `${h}h`;
};

const TaskSearchDropdown = ({ value, onChange, tasks, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = tasks.filter((t) =>
    t.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (title) => {
    onChange(title);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <Input
        placeholder="Search or type task name"
        value={open ? search : value}
        onChange={(e) => {
          if (!open) {
            setOpen(true);
            setSearch(e.target.value);
            onChange(e.target.value);
          } else {
            setSearch(e.target.value);
            onChange(e.target.value);
          }
        }}
        onFocus={() => { if (!disabled) setOpen(true); setSearch(value || ''); }}
        disabled={disabled}
        autoComplete="off"
        data-testid={`${testId}-input`}
      />
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg overflow-hidden" data-testid={`${testId}-dropdown`}>
          {/* Search header */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 dark:border-zinc-800">
            <Search className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
            <input
              className="w-full text-sm bg-transparent outline-none text-zinc-800 dark:text-zinc-200 placeholder-zinc-400"
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); onChange(e.target.value); }}
              autoFocus
              data-testid={`${testId}-search`}
            />
          </div>
          {/* Task list */}
          <div className="max-h-48 overflow-y-auto" data-testid={`${testId}-list`}>
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-zinc-400 text-center">
                {search ? 'No matching tasks — your text will be used as-is' : 'No active tasks found'}
              </div>
            ) : (
              filtered.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
                  onClick={() => handleSelect(t.title)}
                  data-testid={`${testId}-option-${t.id}`}
                >
                  <span className="truncate text-zinc-800 dark:text-zinc-200">{t.title}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Timesheet = () => {
  const { getAuthHeaders } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [entries, setEntries] = useState([emptyEntry()]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState('draft');
  const [tsId, setTsId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tasks, setTasks] = useState([]);

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const isAllowed = selectedDate === today || selectedDate === yesterday;
  const isSubmitted = status === 'submitted';
  const readOnly = isSubmitted || !isAllowed;

  const totalMinutes = entries.reduce((s, e) => s + (parseInt(e.minutes, 10) || 0), 0);
  const pct = Math.min(100, Math.round((totalMinutes / TARGET) * 100));

  // Fetch categories and tasks
  useEffect(() => {
    (async () => {
      try {
        const [catRes, taskRes] = await Promise.all([
          fetch(`${API}/api/timesheets/categories`, { headers: getAuthHeaders() }),
          fetch(`${API}/api/tasks/my-tasks`, { headers: getAuthHeaders() }),
        ]);
        if (catRes.ok) setCategories(await catRes.json());
        if (taskRes.ok) {
          const allTasks = await taskRes.json();
          setTasks(allTasks.filter((t) => t.status !== 'Completed'));
        }
      } catch {}
    })();
  }, [getAuthHeaders]);

  // Fetch timesheet for selected date
  const fetchTimesheet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/timesheets/my?target_date=${selectedDate}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setEntries(data.entries?.length ? data.entries : [emptyEntry()]);
          setStatus(data.status);
          setTsId(data.id);
        } else {
          setEntries([emptyEntry()]);
          setStatus('draft');
          setTsId(null);
        }
      }
    } catch {
      toast.error('Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, getAuthHeaders]);

  useEffect(() => { fetchTimesheet(); }, [fetchTimesheet]);

  // Entry CRUD
  const addEntry = () => setEntries((prev) => [...prev, emptyEntry()]);
  const removeEntry = (id) => setEntries((prev) => prev.filter((e) => e.id !== id));
  const updateEntry = (id, field, value) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));

  // Save draft
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/timesheets/save`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, entries }),
      });
      if (res.ok) {
        const data = await res.json();
        setTsId(data.id);
        setStatus(data.status);
        toast.success('Timesheet saved');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Save failed');
      }
    } catch { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  // Submit
  const handleSubmit = async () => {
    if (totalMinutes === 0) { toast.error('Add at least one task with time'); return; }
    // Save first, then submit
    setSaving(true);
    setSubmitting(true);
    try {
      const saveRes = await fetch(`${API}/api/timesheets/save`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, entries }),
      });
      if (!saveRes.ok) { toast.error('Save failed'); return; }
      const saved = await saveRes.json();

      const subRes = await fetch(`${API}/api/timesheets/${saved.id}/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (subRes.ok) {
        setStatus('submitted');
        setTsId(saved.id);
        toast.success('Timesheet submitted');
      } else {
        const err = await subRes.json();
        toast.error(err.detail || 'Submit failed');
      }
    } catch { toast.error('Network error'); }
    finally { setSaving(false); setSubmitting(false); }
  };

  // Navigate date
  const goDate = (dir) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + dir);
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  const dateLabel = selectedDate === today ? 'Today' : selectedDate === yesterday ? 'Yesterday' : new Date(selectedDate + 'T00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="timesheet-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100" data-testid="timesheet-title">
            My Timesheet
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Log your daily tasks and hours</p>
        </div>
        {/* Date nav */}
        <div className="flex items-center gap-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl px-2 py-1.5" data-testid="timesheet-date-nav">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => goDate(-1)} data-testid="timesheet-prev-day">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[140px] text-center" data-testid="timesheet-date-label">
            {dateLabel} &middot; {selectedDate}
          </span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => goDate(1)} disabled={selectedDate >= today} data-testid="timesheet-next-day">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <Card data-testid="timesheet-progress-card">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-zinc-500" />
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {fmtMin(totalMinutes)} / {fmtMin(TARGET)}
              </span>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
              pct >= 100 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                : pct >= 75 ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
            }`} data-testid="timesheet-progress-pct">
              {pct}%
            </span>
          </div>
          <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all duration-500 ${
                pct >= 100 ? 'bg-emerald-500' : pct >= 75 ? 'bg-blue-500' : 'bg-amber-500'
              }`}
              style={{ width: `${pct}%` }}
              data-testid="timesheet-progress-bar"
            />
          </div>
          {isSubmitted && (
            <div className="flex items-center gap-1.5 mt-2 text-emerald-600 text-xs font-medium">
              <CheckCircle2 className="h-3.5 w-3.5" /> Submitted
            </div>
          )}
          {!isAllowed && !isSubmitted && (
            <div className="flex items-center gap-1.5 mt-2 text-amber-600 text-xs font-medium">
              <AlertTriangle className="h-3.5 w-3.5" /> Timesheets can only be logged for today or yesterday
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entries */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : (
        <div className="space-y-3" data-testid="timesheet-entries">
          {entries.map((entry, idx) => (
            <Card key={entry.id} className="relative" data-testid={`timesheet-entry-${idx}`}>
              <CardContent className="py-4 space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-xs font-bold text-zinc-400 mt-2.5 min-w-[20px]">{idx + 1}</span>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-3">
                    {/* Task name - searchable dropdown */}
                    <div className="sm:col-span-4">
                      <label className="text-xs font-medium text-zinc-500 mb-1 block">Task</label>
                      <TaskSearchDropdown
                        value={entry.task_name}
                        onChange={(val) => updateEntry(entry.id, 'task_name', val)}
                        tasks={tasks}
                        disabled={readOnly}
                        testId={`entry-task-${idx}`}
                      />
                    </div>
                    {/* Category */}
                    <div className="sm:col-span-3">
                      <label className="text-xs font-medium text-zinc-500 mb-1 block">Category</label>
                      <select
                        className="w-full h-9 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={entry.category}
                        onChange={(e) => updateEntry(entry.id, 'category', e.target.value)}
                        disabled={readOnly}
                        data-testid={`entry-category-${idx}`}
                      >
                        <option value="">Select category</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    {/* Minutes */}
                    <div className="sm:col-span-2">
                      <label className="text-xs font-medium text-zinc-500 mb-1 block">Minutes</label>
                      <Input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={entry.minutes || ''}
                        onChange={(e) => updateEntry(entry.id, 'minutes', parseInt(e.target.value, 10) || 0)}
                        disabled={readOnly}
                        data-testid={`entry-minutes-${idx}`}
                      />
                    </div>
                    {/* Description */}
                    <div className="sm:col-span-3">
                      <label className="text-xs font-medium text-zinc-500 mb-1 block">Description</label>
                      <Input
                        placeholder="Brief description"
                        value={entry.description}
                        onChange={(e) => updateEntry(entry.id, 'description', e.target.value)}
                        disabled={readOnly}
                        data-testid={`entry-desc-${idx}`}
                      />
                    </div>
                  </div>
                  {/* Remove button */}
                  {!readOnly && entries.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 mt-5 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => removeEntry(entry.id)} data-testid={`entry-remove-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add entry + actions */}
          {!readOnly && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <Button variant="outline" onClick={addEntry} className="gap-2" data-testid="timesheet-add-entry">
                <Plus className="h-4 w-4" /> Add Task
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={handleSave} disabled={saving} className="gap-2" data-testid="timesheet-save-btn">
                <Save className="h-4 w-4" /> {saving && !submitting ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button onClick={handleSubmit} disabled={saving || submitting} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="timesheet-submit-btn">
                <Send className="h-4 w-4" /> {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Timesheet;
